/**
 * Offline Storage for Chat Messages
 * 
 * Uses IndexedDB to store partial message content during streaming.
 * This allows recovery of partial messages if the stream fails.
 * 
 * Features:
 * - Store partial message content during streaming
 * - Retrieve partial content on stream failure
 * - Automatic cleanup of old partial messages
 * - TypeScript-first API
 * 
 * Usage:
 * ```typescript
 * // Save partial content during streaming
 * await OfflineStorage.savePartial({
 *   id: 'msg-123',
 *   content: 'Partial content...',
 *   timestamp: Date.now()
 * });
 * 
 * // Retrieve on error
 * const partial = await OfflineStorage.getPartial('msg-123');
 * 
 * // Delete after successful completion
 * await OfflineStorage.deletePartial('msg-123');
 * ```
 */

const DB_NAME = 'orion_chat_offline';
const DB_VERSION = 1;
const STORE_NAME = 'partial_messages';

export interface PartialMessage {
    id: string;
    content: string;
    timestamp: number;
    conversationId?: string;
    agentId?: string;
}

/**
 * Initialize IndexedDB database.
 */
function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'));
        };

        request.onsuccess = () => {
            resolve(request.result);
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            // Create object store if it doesn't exist
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                objectStore.createIndex('conversationId', 'conversationId', { unique: false });
            }
        };
    });
}

/**
 * Offline Storage Service
 */
export class OfflineStorage {
    /**
     * Save partial message content.
     */
    static async savePartial(message: PartialMessage): Promise<void> {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.put(message);

                request.onsuccess = () => {
                    resolve();
                };

                request.onerror = () => {
                    reject(new Error('Failed to save partial message'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to save partial:', err);
            throw err;
        }
    }

    /**
     * Get partial message content by ID.
     */
    static async getPartial(id: string): Promise<PartialMessage | null> {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.get(id);

                request.onsuccess = () => {
                    resolve(request.result || null);
                };

                request.onerror = () => {
                    reject(new Error('Failed to get partial message'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to get partial:', err);
            return null;
        }
    }

    /**
     * Delete partial message by ID.
     */
    static async deletePartial(id: string): Promise<void> {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.delete(id);

                request.onsuccess = () => {
                    resolve();
                };

                request.onerror = () => {
                    reject(new Error('Failed to delete partial message'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to delete partial:', err);
            throw err;
        }
    }

    /**
     * Get all partial messages.
     */
    static async getAllPartials(): Promise<PartialMessage[]> {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.getAll();

                request.onsuccess = () => {
                    resolve(request.result || []);
                };

                request.onerror = () => {
                    reject(new Error('Failed to get all partial messages'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to get all partials:', err);
            return [];
        }
    }

    /**
     * Cleanup old partial messages.
     * @param olderThan - Delete messages older than this timestamp (default: 24 hours)
     */
    static async cleanup(olderThan?: number): Promise<number> {
        const cutoffTime = olderThan || Date.now() - 24 * 60 * 60 * 1000; // 24 hours

        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');

            return new Promise((resolve, reject) => {
                const range = IDBKeyRange.upperBound(cutoffTime);
                const request = index.openCursor(range);
                let deletedCount = 0;

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

                    if (cursor) {
                        cursor.delete();
                        deletedCount++;
                        cursor.continue();
                    } else {
                        console.log(`[OfflineStorage] Cleaned up ${deletedCount} old partial messages`);
                        resolve(deletedCount);
                    }
                };

                request.onerror = () => {
                    reject(new Error('Failed to cleanup partial messages'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to cleanup:', err);
            return 0;
        }
    }

    /**
     * Clear all partial messages.
     */
    static async clearAll(): Promise<void> {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.clear();

                request.onsuccess = () => {
                    console.log('[OfflineStorage] Cleared all partial messages');
                    resolve();
                };

                request.onerror = () => {
                    reject(new Error('Failed to clear partial messages'));
                };
            });
        } catch (err) {
            console.error('[OfflineStorage] Failed to clear all:', err);
            throw err;
        }
    }
}
